import React, { useEffect, useState } from 'react';
import { Link, useLocation, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { LayoutDashboard, FileText, ListTodo, FolderOpen, CreditCard, LogOut, Plus, Receipt, Download, Calendar, MessageSquare, UploadCloud, FileSpreadsheet, Link2, Users } from 'lucide-react';
import NotificationBell from '../components/NotificationBell';
import { useTenant } from '../context/TenantContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const ClientLayout = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isOperator } = useAuth();
  const { activeTenant, tenants, setActiveTenant, onboardingStatus, refreshOnboardingStatus } = useTenant();
  const [reviewOpen, setReviewOpen] = useState(false);

  const isReadyToOperate = onboardingStatus?.is_ready_to_operate === true;
  const onboardingState = onboardingStatus?.onboarding_status || 'DRAFT';
  const hasSubmittedOnboarding = onboardingState !== 'DRAFT';

  const basePath = '/client';
  const activeTenantLabel = tenants.find((tenant) => tenant.id === activeTenant)?.name || activeTenant;
  const navItems = [
    { path: '/client/dashboard', icon: LayoutDashboard, label: t('dashboard') },
    { path: '/client/requests', icon: FileText, label: t('requests') },
    { path: '/client/invoices', icon: FileText, label: 'Facturacion', requiresReady: true, gated: true },
    { path: '/client/expenses', icon: Receipt, label: 'Gastos', requiresReady: true, gated: true },
    { path: '/client/cierre-mensual', icon: Calendar, label: 'Cierre Mensual', requiresReady: true, gated: true },
    { path: '/client/tasks', icon: ListTodo, label: t('tasks') },
    { path: '/client/documents', icon: FolderOpen, label: t('documents') },
    { path: '/client/users', icon: Users, label: 'Colaboradores', requiresReady: false, gated: true },
    { path: '/client/templates', icon: FileSpreadsheet, label: 'Plantillas', requiresReady: false, gated: true },
    { path: '/client/imports', icon: UploadCloud, label: 'Importaciones', requiresReady: false, gated: true },
    { path: '/client/holded-exports', icon: Download, label: 'Exportaciones Holded', requiresReady: false, gated: true },
    { path: '/client/reports', icon: FileText, label: 'Reportes', requiresReady: true, gated: true },
    { path: '/client/integrations/holded', icon: Link2, label: 'Holded', requiresReady: false, gated: true },
    { path: '/client/exports', icon: Download, label: 'Exportar', requiresReady: false, gated: true },
    { path: '/client/billing', icon: CreditCard, label: 'Subscripcion' },
    { path: '/client/chat', icon: MessageSquare, label: 'Chat', requiresReady: false, gated: true },
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  useEffect(() => {
    refreshOnboardingStatus();
  }, []);

  return (
    <div className="min-h-screen bg-background">
      {/* Top Navigation */}
      <header className="sticky top-0 z-50 bg-card border-b border-border shadow-sm" data-testid="client-header">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          {/* Logo */}
          <Link to={`${basePath}/dashboard`} className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center">
              <span className="text-primary-foreground font-bold text-xl font-heading">Z</span>
            </div>
            <h1 className="text-2xl font-heading font-bold text-primary">Zentro</h1>
          </Link>

          {/* Navigation */}
          <nav className="hidden md:flex items-center gap-2" data-testid="client-nav">
            {navItems
              .filter((item) => !item.gated || hasSubmittedOnboarding)
              .map((item) => {
              const Icon = item.icon;
              const resolvedPath = item.path.replace('/client', basePath);
              const isActive = location.pathname === resolvedPath;
              const blocked = item.requiresReady && !isReadyToOperate;
              const handleClick = (e) => {
                if (blocked) {
                  e.preventDefault();
                  setReviewOpen(true);
                }
              };
              return (
                <Link
                  key={item.path}
                  to={resolvedPath}
                  onClick={handleClick}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all hover:scale-105 ${
                    isActive
                      ? 'bg-primary text-primary-foreground shadow-md shadow-primary/20'
                      : 'hover:bg-accent'
                  } ${blocked ? 'opacity-60' : ''}`}
                  data-testid={`nav-${item.path.split('/').pop()}`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="font-medium">{item.label}</span>
                </Link>
              );
            })}
          </nav>

          {/* User Menu */}
          <div className="flex items-center gap-3">
            {/* New Request Button */}
            <Button 
              onClick={() => navigate(`${basePath}/requests/new`)}
              size="sm"
              className="hidden sm:flex gap-2"
              data-testid="new-request-btn"
            >
              <Plus className="w-4 h-4" />
              Nueva Solicitud
            </Button>

            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`${basePath}/dashboard`)}
              className="sm:hidden"
              data-testid="client-dashboard-link"
            >
              Panel
            </Button>

            {isOperator() && tenants.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="hidden sm:flex justify-between min-w-[180px]"
                    data-testid="tenant-switcher-client"
                  >
                    <span className="truncate">{activeTenantLabel || 'Seleccionar'}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56">
                  {tenants.map((tenant) => (
                    <DropdownMenuItem
                      key={tenant.id}
                      onClick={() => setActiveTenant(tenant.id)}
                      data-testid={`client-tenant-option-${tenant.id}`}
                    >
                      {tenant.name || tenant.id}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            
            {/* Notifications */}
            <NotificationBell />
            
            <div className="hidden sm:flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                <span className="text-primary font-semibold">{user?.full_name?.charAt(0) || 'U'}</span>
              </div>
              <div>
                <p className="text-sm font-medium">{user?.full_name}</p>
                <p className="text-xs text-muted-foreground">{user?.email}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="gap-2"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">{t('logout')}</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        {hasSubmittedOnboarding && !isReadyToOperate && (
          <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-amber-900">
            {onboardingState === 'REJECTED'
              ? `Tu incorporacion fue rechazada. Motivo: ${onboardingStatus?.onboarding_reject_reason || 'Pendiente'}.`
              : 'Tu cuenta esta en revision. Te avisaremos por email cuando este lista.'}
          </div>
        )}
        <Outlet />
      </main>

      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cuenta en revision</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Tu incorporacion esta en revision. Cuando este aprobada podras usar este modulo.
          </p>
          <Button onClick={() => setReviewOpen(false)} className="mt-4">Entendido</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientLayout;

